import { useCompositionContext } from '@src/components/sidepanel/CompositionContext';
import { CompositionSaveTemplate } from '@src/components/sidepanel/CompositionSettings/CompositionSaveTemplate';
import { standardizeControlLabels } from '@src/services/composition/templateControlNormalization';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import {
  calculateUpdatedControlValues,
  getDimensionSize,
  modifyCompositionProperty,
  upsertUpdatedValue,
} from '@src/utility/compositionSettings';
import {
  mergeElementLayout,
  readElementLayout,
  unwrapLiveCardSource,
} from '@src/utility/quoteCardLayout';
import { adjustBackgroundImageAspectRatio } from '@src/utility/quoteCardsCompositionSettings';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import {
  convertFromPixels,
  convertToPixels,
} from '@src/utility/videoTranscriptionUtils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CompositionControl } from '../../sidepanel/CompositionSettings/CompositionControl';
import {
  type CompositionTemplate,
  type ControlConfiguration,
  QuoteControlType,
} from '../../sidepanel/CompositionSettings/types';

import type { Speaker } from '../SpeakerTypes';
import type { ElementState } from '@src/renderer/ElementState';
import type { Renderer } from '@src/renderer/Renderer';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { ExtendedAspectRatio } from '@src/types.ts/video';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { ElementPlacementParts } from '@src/utility/quoteCardLayout';

interface QuoteCardSettingsPanelProps {
  compositionTemplate: CompositionTemplate;
  activeElement: ElementState | null;
  dimension: ExtendedAspectRatio;
  editorStore: EditorStoreLike | null;
  videoCreator: IVideoCreatorStore;
  rendererRef: React.RefObject<Renderer>;
  metadata: Record<string, any>;
  handleSpeakerControlChange: (
    inputMapping: Record<string, string>,
    speaker: Speaker,
  ) => void;
  onAfterCreate: (composition: CompositionTemplate) => void;
  activeComposition: ElementState | null;
  /** Bumped by the renderer hook on every canvas edit so the live source is re-read. */
  editRevision?: number;
}

export const QuoteCardSettingsPanel: React.FC<QuoteCardSettingsPanelProps> = ({
  compositionTemplate,
  activeElement,
  dimension,
  videoCreator,
  rendererRef,
  metadata,
  handleSpeakerControlChange,
  onAfterCreate,
  activeComposition,
  editorStore,
  // Read for its re-render; the live source is re-read on every render.
  editRevision: _editRevision = 0,
}) => {
  const userIdentity = useUserIdentity();
  const { enableShareableImageSaveUpdateTemplate } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const {
    selectedCategory,
    selectedSubcategory,
    getCompositionWithCategoryAndSubcategoryById,
  } = useCompositionContext();
  // Save into the template's own category, not the Template tab's selection,
  // which is empty unless that tab was opened (PLAT-11977).
  const templatePlacement = useMemo(
    () => getCompositionWithCategoryAndSubcategoryById(compositionTemplate.id),
    [getCompositionWithCategoryAndSubcategoryById, compositionTemplate.id],
  );
  const recalculationTrigger = useRef<boolean>(false);
  const [updatedValues, setUpdatedValues] = useState<
    {
      elementPaths: number[][];
      value: unknown;
      property: string;
    }[]
  >([]);

  // Renumber generic Creatomate placeholder text labels ("Text-TLB" → "Text-1")
  // for display, mirroring CompositionControls. Cosmetic (matching is by
  // property + elementPaths); flows to the control rows and the standards modals.
  const relabeledControls = useMemo(
    () =>
      standardizeControlLabels(
        compositionTemplate.source,
        compositionTemplate.controls,
      ),
    [compositionTemplate.source, compositionTemplate.controls],
  );

  // Diff against the card COMPOSITION, which the control paths are relative
  // to — never the element the user happens to have selected on the canvas.
  // With a child selected, no path resolved and the font-size fallback read
  // that child's size as every text control's value (PLAT-11977).
  const diffRoot = activeComposition ?? activeElement;
  useEffect(() => {
    if (diffRoot) {
      const values = calculateUpdatedControlValues({
        controls: compositionTemplate.controls,
        activeElement: diffRoot,
        extraElementData: videoCreator.currentVideo?.extraElementData,
        organization: videoCreator.organization,
        story: videoCreator.story,
      });
      // Direct edits (modifyProperty) win over the lagging value-diff recompute,
      // so a just-changed color/font shows immediately (mirrors CompositionControls).
      setUpdatedValues(prev =>
        prev.reduce((acc, entry) => upsertUpdatedValue(acc, entry), values),
      );
    }
  }, [
    compositionTemplate.controls,
    diffRoot,
    videoCreator.currentVideo,
    recalculationTrigger.current,
  ]);

  const modifyProperty = async (
    control: ControlConfiguration,
    value: unknown,
  ) => {
    if (!control.elementPaths || !control?.property || !editorStore) return;
    const elementPaths = control.elementPaths;
    const property = control?.property;

    modifyCompositionProperty(
      activeComposition?.source.id || '',
      editorStore,
      elementPaths,
      property,
      value,
    );

    // Record the edit directly so it survives the lagging value-diff recompute
    // and shows immediately in Update Standards (mirrors CompositionControls).
    recalculationTrigger.current = !recalculationTrigger.current;
    setUpdatedValues(prev =>
      upsertUpdatedValue(prev, { elementPaths, value, property }),
    );

    if (
      control.default?.quote === QuoteControlType.BACKGROUND &&
      activeComposition?.source
    ) {
      const dimensions = getDimensionSize(dimension);

      const source = rendererRef.current?.getSource();
      const results = await adjustBackgroundImageAspectRatio(
        {
          ...source,
          width: dimensions.width,
          height: dimensions.height,
        },
        {
          ...control,
          elementPaths: control.elementPaths.map(path => [0, ...path]),
        },
        value as string,
      );
      if (!results) return;

      for (const result of results) {
        editorStore.editor.commandUpdateElement(
          result.elementId,
          {
            width: result.width,
            height: result.height,
          },
          false,
        );
      }
    }
  };

  // Transient hover preview: apply the hovered color to the quote-card editor
  // (the same dedicated editor `modifyProperty` commits to) without an undo
  // entry, so the card previews the color live. ColorControl debounces these
  // calls and restores the committed color on hover-out. Without this, hover
  // preview would target the global store and never reach the card's renderer.
  const previewProperty = (control: ControlConfiguration, value: unknown) => {
    if (!control.elementPaths || !control.property || !editorStore) return;
    modifyCompositionProperty(
      activeComposition?.source.id || '',
      editorStore,
      control.elementPaths,
      control.property,
      value,
      false,
    );
  };

  const onImageScaleChange = (
    control: ControlConfiguration,
    value: unknown,
  ) => {
    if (!control.elementPaths || !editorStore) return;

    const elementPaths = control.elementPaths;

    modifyCompositionProperty(
      activeComposition?.source.id || '',
      editorStore,
      elementPaths,
      'x_scale',
      value,
    );
    modifyCompositionProperty(
      activeComposition?.source.id || '',
      editorStore,
      elementPaths,
      'y_scale',
      value,
    );
  };

  const { width, height } = getDimensionSize(dimension);

  // When CreateComposition is called in useQuoteCard, elements are nested within
  // a single composition element instead of appearing at the root level.
  // Extract the nested elements so controls continue to work while preserving
  // updated positions, colors, styles, and animations.
  const compositionSource = rendererRef.current?.getSource();
  const liveCardSource = unwrapLiveCardSource(compositionSource);
  const compositionTemplateToSave = {
    ...compositionTemplate,
    controls: relabeledControls,
    source: liveCardSource ?? compositionTemplate.source,
  };

  // Elements the user moved or resized on the canvas (logo, headshot …),
  // measured against the template's saved layout. Drives the modal's
  // Position / Scale rows. Computed per render on purpose: the renderer source
  // is read fresh each time, and `editRevision` re-renders us after a drag.
  const elementLayout = readElementLayout(
    compositionTemplate.source,
    liveCardSource,
    compositionTemplate.controls,
  );
  const positionDirty = elementLayout.some(
    e => e.positionChanged || e.sizeChanged,
  );

  // "Set as Graphic Standard" writes the template's own source with only the
  // moved geometry merged in, so one card's text, colours and canvas never
  // become the template (PLAT-11977). A NEW template (create / save_as) is a
  // snapshot of this card by design, so it keeps the live source.
  const getSourceForSave = (parts: ElementPlacementParts, action: string) =>
    action === 'update'
      ? mergeElementLayout(
          compositionTemplate.source,
          liveCardSource,
          parts,
          compositionTemplate.controls,
        )
      : compositionTemplateToSave.source;

  return (
    <SettingsContainer>
      {activeComposition && (
        <>
          {relabeledControls
            .filter(control => control.enabled !== false)
            .map((controlConfig: ControlConfiguration, idx: number) => (
              <ControlWrapper
                key={`${controlConfig.property || controlConfig.label}-${idx}`}
              >
                <CompositionControl
                  control={controlConfig}
                  activeElement={
                    activeComposition ||
                    ({ id: 'preset' } as unknown as ElementState)
                  }
                  modifyProperty={modifyProperty}
                  previewProperty={previewProperty}
                  handleSpeakerControlChange={handleSpeakerControlChange}
                  // Four decimals: the raw ratio (2.5925925925925926) was
                  // stored and shown verbatim and read as a change on every
                  // round trip (PLAT-11977).
                  convertToVh={px =>
                    Number(
                      convertFromPixels(parseFloat(px || '32'), 'vh', {
                        width: width,
                        height: height,
                      }).toFixed(4),
                    ) + ' vh'
                  }
                  convertToPx={value => {
                    if (value == null || value === '') return null;
                    if (typeof value === 'number') return value;
                    const parsed = parseFloat(value);
                    if (!Number.isFinite(parsed)) return null;
                    const unit = value.includes('vh')
                      ? 'vh'
                      : value.includes('vw')
                        ? 'vw'
                        : null;
                    return unit
                      ? convertToPixels(parsed, unit, { width, height })
                      : parsed;
                  }}
                  compositionMeta={metadata}
                  onImageScaleChange={onImageScaleChange}
                />
              </ControlWrapper>
            ))}
        </>
      )}
      {activeComposition && enableShareableImageSaveUpdateTemplate && (
        <CompositionSaveTemplate
          composition={compositionTemplateToSave}
          updatedValues={updatedValues}
          categoryId={
            (templatePlacement.category ?? selectedCategory)?.id || ''
          }
          subcategoryId={
            (templatePlacement.subcategory ?? selectedSubcategory)?.id || ''
          }
          activeElement={activeComposition}
          editorStore={editorStore || undefined}
          onAfterCreate={onAfterCreate}
          onAfterSave={() => setUpdatedValues([])}
          saveMeta={false}
          positionDirty={positionDirty}
          getSourceForSave={getSourceForSave}
          elementLayout={elementLayout}
          standardSource={compositionTemplate.source}
        />
      )}
    </SettingsContainer>
  );
};

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-bottom: 24px;
`;

const ControlWrapper = styled.div`
  display: flex;
  flex-direction: column;

  &:empty {
    display: none;
  }
`;
